import { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table, {
  TableColumn,
  TableMobileRowContext,
  TableProps,
} from "@/components/table";
import { TrashIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  Application,
  ApplicationCurrentApproval,
  ApplicationPendingApprover,
  OverallStatus,
} from "@/types/application";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router-dom";
import {
  useDiscardApplication,
  useRetryErroredApplications,
} from "@/hooks/useApplication";
import { useToast } from "@ui/toast";
import { ApplicationStatusTag } from "../application-status-tag";
import DiscardModal from "../modals/discard-modal";
import { useModal } from "@/hooks/useModal";
import { ArrowUpDown } from "lucide-react";
import { useUsersByIds } from "@/hooks/useMasterData";
import { Avatar } from "@ui/avatar";
import { getApiErrorMessage } from "@/utils/api-error";

import ApplicationStatusMenu from "@ui/application-status-menu";

interface ApplicationTableProps {
  applications: Application[];
  loading?: boolean;
  empty?: JSX.Element;
  urlPrefix?: string;
  adminView?: boolean;
  showApplicant?: boolean;
  showAction?: boolean;
  filter?: {
    value: OverallStatus | null;
    onFilter: (filter: OverallStatus | null) => void;
  };
  sorter?: {
    value: "asc" | "desc";
    onSorter: (sorter: "asc" | "desc") => void;
  };
  pagination?: TableProps<Application>["pagination"];
}

function shouldShowPendingApprovers(app: Application) {
  return app.overallStatus === OverallStatus.InProgress;
}

function uniqueApprovers(
  approvers: ApplicationPendingApprover[],
): ApplicationPendingApprover[] {
  const seen = new Set<number>();
  return approvers.filter((approver) => {
    if (seen.has(approver.id)) return false;
    seen.add(approver.id);
    return true;
  });
}

function renderApprovalSummary(
  nodeDescriptions: Array<string | null | undefined>,
  pendingApprovers: ApplicationPendingApprover[],
) {
  const approvers = uniqueApprovers(pendingApprovers);
  const firstApprover = approvers[0];
  const additionalApproverCount = Math.max(0, approvers.length - 1);
  const description = nodeDescriptions
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))
    .join(" / ");

  if (!firstApprover) {
    return (
      <span className="text-base font-medium leading-6 text-dark">--</span>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col justify-center gap-0.5 py-2.5">
      {description && (
        <div
          className="w-full min-w-0 truncate text-dark font-medium"
          title={description}
        >
          {description}
        </div>
      )}
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex min-w-0 items-center gap-1">
          <Avatar name={firstApprover.name} colorScheme="purple" size="sm" />
          <span className="min-w-0 truncate text-xs font-medium leading-5 text-secondary-text">
            {firstApprover.name}
          </span>
        </div>
        {additionalApproverCount > 0 && (
          <span className="flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full border border-stroke px-2 text-xs font-normal leading-5 text-secondary-text">
            +{additionalApproverCount}
          </span>
        )}
      </div>
    </div>
  );
}

function renderCurrentApproval(app: Application) {
  if (!shouldShowPendingApprovers(app)) {
    return (
      <span className="text-base font-medium leading-6 text-dark">--</span>
    );
  }

  const currentApprovals: ApplicationCurrentApproval[] =
    app.currentApprovals ?? [];

  if (currentApprovals.length > 0) {
    return renderApprovalSummary(
      currentApprovals.map((approval) => approval.description),
      currentApprovals.flatMap((approval) => approval.approvers),
    );
  }

  return renderApprovalSummary(
    [app.currentNodeDescription ?? app.currentWorkflowNodeDescription],
    app.pendingApprovers ?? [],
  );
}

export function ApplicationTable({
  applications,
  loading,
  empty,
  urlPrefix = "",
  adminView = false,
  showApplicant = false,
  showAction = true,
  pagination,
  ...props
}: ApplicationTableProps) {
  const { t } = useTranslation();
  const userIds = useMemo(
    () => applications.map((app) => String(app.applicantId) ?? app.submittedBy),
    [applications],
  );
  const { users } = useUsersByIds(userIds);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { mutate: discardApplication, isPending: isCancelling } =
    useDiscardApplication({
      onSuccess: () => {
        closeDiscardModal();
        toast({
          variant: "success",
          title: "Successfully discard application.",
        });
      },
    });
  const { toast } = useToast();
  const [retryingSerialNumber, setRetryingSerialNumber] = useState<
    string | null
  >(null);
  const { mutate: retryErroredApplications } = useRetryErroredApplications({
    onSuccess: (data, serialNumbers) => {
      const serialNumber = serialNumbers[0];
      const result = data.data?.find(
        (item) => item.serialNumber === serialNumber,
      );

      if (result?.success) {
        toast({
          variant: "success",
          title: t("application_page.retry_success_title"),
          description: result.message,
        });
      } else {
        toast({
          variant: "destructive",
          title: t("application_page.retry_failed_title"),
          description: result?.message,
        });
      }

      setRetryingSerialNumber(null);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: t("application_page.retry_failed_title"),
        description: getApiErrorMessage(error),
      });
      setRetryingSerialNumber(null);
    },
  });
  const [activeApplication, setActiveApplication] =
    useState<Application | null>(null);
  const {
    open: openDiscardModal,
    isOpen: isDiscardModalOpen,
    close: closeDiscardModal,
  } = useModal();

  function onClickDiscard(application: Application) {
    setActiveApplication(application);
    openDiscardModal();
  }
  function onDiscard(applicationId: string) {
    discardApplication(applicationId);
  }

  const canDiscardApplication = useCallback(
    (app: Application) =>
      (app.overallStatus === OverallStatus.InProgress ||
        app.overallStatus === OverallStatus.Draft) &&
      (app.allowDiscard ?? true),
    [],
  );
  const canRetryErroredApplication = useCallback(
    (app: Application) =>
      adminView && app.overallStatus === OverallStatus.Error,
    [adminView],
  );

  function onClickRetry(application: Application) {
    setRetryingSerialNumber(application.serialNumber);
    retryErroredApplications([application.serialNumber]);
  }

  useEffect(() => {
    const totalPages =
      pageSize > 0 ? Math.max(1, Math.ceil(applications.length / pageSize)) : 1;
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, pageSize, applications.length]);

  const handleRowClick = useCallback(
    (app: Application) => {
      const adminUrl = adminView ? "/admin" : "";
      navigate(`${urlPrefix}${adminUrl}/application/${app.serialNumber}`);
    },
    [adminView, navigate, urlPrefix],
  );

  const desktopTableClassName = showApplicant
    ? showAction
      ? "min-w-[1360px]"
      : "min-w-[1200px]"
    : showAction
      ? "min-w-[1120px]"
      : "min-w-[960px]";

  const columns = useMemo<TableColumn<Application>[]>(() => {
    return [
      {
        key: "application",
        header: t("dashboard.application_name"),
        headerClassName: "w-[360px] min-w-[360px]",
        cellClassName: "w-[360px] min-w-[360px]",
        cell: (app) => (
          <div className="flex min-w-0 items-center gap-3 py-2.5">
            <div className="min-w-0 max-w-full">
              <div className="font-body-medium-medium break-words text-dark">
                {app.formInstance.form.name}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-body-small-medium text-secondary-text">
                  #{app.serialNumber}
                </span>
              </div>
              {app.summary && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="break-words text-sm font-medium text-primary-text">
                    {app.summary}
                  </span>
                </div>
              )}
            </div>
          </div>
        ),
      },
      ...(showApplicant
        ? [
            {
              key: "applicant",
              header: t("dashboard.applicant", { defaultValue: "Applicant" }),
              headerClassName: "w-56",
              cellClassName: "w-56",
              cell: (app: Application) => (
                <div className="flex items-center gap-2.5">
                  <Avatar
                    name={
                      users?.find((u) => u.id === String(app.applicantId))?.name
                    }
                    colorScheme="blue"
                    size="sm"
                  />
                  <div className="text-base font-medium text-dark leading-6 line-clamp-1">
                    {users?.find((u) => u.id === String(app.applicantId))
                      ?.name ?? "-"}
                  </div>
                </div>
              ),
            } satisfies TableColumn<Application>,
          ]
        : []),
      {
        key: "status",
        header: (
          <div className="flex min-w-0 flex-row items-center justify-between gap-2">
            <span className="min-w-0 truncate">{t("dashboard.status")}</span>
            {props.filter && (
              <ApplicationStatusMenu onItemSelect={props.filter.onFilter} />
            )}
          </div>
        ),
        headerClassName: "w-[152px] min-w-[152px] max-w-[152px]",
        cellClassName: "w-[152px] min-w-[152px] max-w-[152px] overflow-hidden",
        cell: (app) => (
          <div className="min-w-0">
            <ApplicationStatusTag
              status={app.overallStatus}
              className="max-w-full whitespace-nowrap"
            />
          </div>
        ),
      },
      {
        key: "current_approval",
        header: t("dashboard.current_approval", {
          defaultValue: "Current approval",
        }),
        headerClassName: "w-[280px] min-w-[280px] max-w-[280px]",
        cellClassName: "w-[280px] min-w-[280px] max-w-[280px] overflow-hidden",
        cell: renderCurrentApproval,
      },
      {
        key: "apply_date",
        header: (
          <div
            className="flex justify-between items-center gap-2 cursor-pointer w-full"
            onClick={() =>
              props.sorter?.onSorter(
                props.sorter.value === "asc" ? "desc" : "asc",
              )
            }
          >
            {t("dashboard.apply_date")}
            {props.sorter && (
              <ArrowUpDown className="text-secondary-text h-4 w-4" />
            )}
          </div>
        ),
        headerClassName: "w-[150px] min-w-[150px] max-w-[150px]",
        cellClassName: "w-[150px] min-w-[150px] max-w-[150px]",
        cell: (app) => (
          <span className="whitespace-nowrap text-base font-medium text-gray-800">
            {app.submittedAt
              ? dayjs(app.submittedAt).format("YYYY-MM-DD")
              : "-"}
          </span>
        ),
      },
      ...(showAction
        ? [
            {
              key: "actions",
              header: t("dashboard.actions"),
              headerClassName: "w-[160px] min-w-[160px] max-w-[160px]",
              cellClassName: "w-[160px] min-w-[160px] max-w-[160px]",
              cell: (app: Application) => {
                const canRetry = canRetryErroredApplication(app);
                const canDiscard = canDiscardApplication(app);

                if (!canRetry && !canDiscard) return null;

                return (
                  <div className="flex items-center gap-2">
                    {canRetry && (
                      <Button
                        type="button"
                        size="sm"
                        className="bg-gray-700 px-3 py-[3px] text-sm font-medium leading-[22px] text-white hover:bg-gray-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClickRetry(app);
                        }}
                        loading={retryingSerialNumber === app.serialNumber}
                      >
                        {t("application_page.retry_error_action")}
                      </Button>
                    )}
                    {canDiscard && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-gray-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClickDiscard(app);
                        }}
                        loading={isCancelling}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </Button>
                    )}
                  </div>
                );
              },
            } satisfies TableColumn<Application>,
          ]
        : []),
    ];
  }, [
    isCancelling,
    retryingSerialNumber,
    canDiscardApplication,
    canRetryErroredApplication,
    props.filter,
    props.sorter,
    showAction,
    showApplicant,
    t,
    users,
  ]);

  // const paginatedApplications = useMemo(() => {
  //   if (pageSize <= 0) return applications;
  //   const start = (currentPage - 1) * pageSize;
  //   return applications.slice(start, start + pageSize);
  // }, [currentPage, pageSize, applications]);

  const renderMobileRow = useCallback(
    ({ row }: TableMobileRowContext<Application>) => (
      <div
        className="flex flex-col gap-4 border-b border-b-stroke p-4 last:border-none"
        key={row.serialNumber}
        onClick={() => handleRowClick(row)}
      >
        <div className="flex min-w-0 flex-col gap-1">
          <div className="font-body-medium-medium break-words text-dark">
            {row.formInstance.form.name}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-body-small-medium text-secondary-text">
              #{row.serialNumber}
            </span>
            <span className="text-sm font-medium text-secondary-text">
              {row.submittedAt
                ? dayjs(row.submittedAt).format("YYYY-MM-DD")
                : "-"}
            </span>
          </div>
          {row.summary && (
            <p className="break-words text-sm font-medium text-primary-text">
              {row.summary}
            </p>
          )}
          <div className="flex min-w-0 flex-col gap-1 pt-1">
            <span className="text-xs font-medium text-secondary-text">
              {t("dashboard.current_approval", {
                defaultValue: "Current approval",
              })}
            </span>
            {renderCurrentApproval(row)}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-row items-center gap-2 justify-between w-full">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <ApplicationStatusTag status={row.overallStatus} />
              {showApplicant && (
                <div className="flex min-w-0 items-center gap-2">
                  <Avatar
                    name={
                      users?.find((u) => u.id === String(row.applicantId))?.name
                    }
                    colorScheme="blue"
                    size="sm"
                  />
                  <span className="min-w-0 truncate text-sm font-medium text-dark">
                    {users?.find((u) => u.id === String(row.applicantId))
                      ?.name ?? "-"}
                  </span>
                </div>
              )}
            </div>
            {showAction &&
              (canRetryErroredApplication(row) ||
                canDiscardApplication(row)) && (
                <div className="flex shrink-0 items-center gap-2">
                  {canRetryErroredApplication(row) && (
                    <Button
                      type="button"
                      size="sm"
                      className="bg-gray-700 px-3 py-[3px] text-sm font-medium leading-[22px] text-white hover:bg-gray-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickRetry(row);
                      }}
                      loading={retryingSerialNumber === row.serialNumber}
                    >
                      {t("application_page.retry_error_action")}
                    </Button>
                  )}
                  {canDiscardApplication(row) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickDiscard(row);
                      }}
                      loading={isCancelling}
                    >
                      <TrashIcon className="h-5 w-5 text-primary-text" />
                    </Button>
                  )}
                </div>
              )}
          </div>
        </div>
      </div>
    ),
    [
      handleRowClick,
      isCancelling,
      retryingSerialNumber,
      canDiscardApplication,
      canRetryErroredApplication,
      onClickDiscard,
      showAction,
      showApplicant,
      t,
      users,
    ],
  );

  return (
    <>
      <Table<Application>
        data={applications}
        columns={columns}
        renderMobileRow={renderMobileRow}
        loading={loading}
        emptyState={empty ?? <Empty />}
        onRowClick={handleRowClick}
        desktopTableClassName={desktopTableClassName}
        pagination={
          pagination
            ? {
                ...pagination,
              }
            : undefined
        }
      />
      <DiscardModal
        isOpen={isDiscardModalOpen}
        close={closeDiscardModal}
        application={activeApplication}
        onDiscard={onDiscard}
      />
    </>
  );
}
function Empty() {
  return (
    <div className="flex flex-col w-full h-full items-center align-middle justify-center">
      <span className="text-primary-text text-lg font-semibold">
        No pending applications
      </span>
      <span className="text-secondary-text">
        You can click{" "}
        <Link
          to={"/application/history?tab=application"}
          className="text-giant-blue"
        >
          here
        </Link>{" "}
        to view your application history.
      </span>
    </div>
  );
}
